import React, { useState, FC, useEffect, useRef, useMemo } from "react"
import { useSelector, useDispatch } from 'react-redux';
import {
  Box, 
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid, 
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack, 
  TextField,
  Typography,
} from '@mui/material';

import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef
} from 'material-react-table';

import { useGetClinicalTrialsQuery } from "../../features/clinicalTrials";
import TrialDetails from './trialdetails';
import { useMatchTrialsMutation } from './trial_recommendation';

const PROGRESS_STEPS = [
  'Understanding requirements…',
  'Analyzing trials…',
  'Filtering trials…',
  'Preparing results…',
];


//If using TypeScript, define the shape of your data (optional, but recommended)
interface TrialTable {
  nctId?: string;
  title: string;
  state: string; // will be single state or "Multiple"
  sponsor: string;
}

type EligibilityRaw = {
  eligibilityCriteria?: string;
  healthyVolunteers?: boolean;
  sex?: string;
  minimumAge?: string;
};

type SiteLocation = {
  city?: string;
  state?: string;
  zip?: string;
  lat?: number;
  lng?: number;
};

type ClinicalTrial = {
  nctId?: string;
  description?: string;
  briefSummary?: string;
  eligibilityCriteria?: string;
  healthyVolunteers?: boolean;
  sex?: string;
  minimumAge?: string;
  sponsorInfo?: { leadSponsor?: { name?: string } };
  site_location: SiteLocation[];
};

type ClinicalTrialRaw = {
  nctId?: string;
  description?: string;
  briefSummary?: string;
  sponsorInfo?: { leadSponsor?: { name?: string } };
  eligibility?: EligibilityRaw; // ✅ nested like your sample
  city?: string;
  state?: string;
  zip?: string;
  lat?: number;
  lng?: number;
};

export interface MatchTrialsResponse {
  matches: {
    nctId: string;
    reason: string;
  }[];
}

// Deduplication logic to raw trial data from clinicaltrial API 
const normalizeTrials = (rows: ClinicalTrialRaw[]): ClinicalTrial[] => {
  type Acc = {
    nctId: string;
    description?: string;
    briefSummary?: string;
    eligibilityCriteria?: string;
    healthyVolunteers?: boolean;
    sex?: string;
    minimumAge?: string;
    sponsorInfo?: ClinicalTrialRaw["sponsorInfo"];
    site_location: SiteLocation[];
    siteKeySet: Set<string>;
  };

  const map = new Map<string, Acc>();

  const locKey = (l: SiteLocation) =>
    `${l.city ?? ''}|${l.state ?? ''}|${l.zip ?? ''}|${l.lat ?? ''}|${l.lng ?? ''}`;

  for (const r of rows) {
    const nctId = r.nctId?.trim();
    if (!nctId) continue;

    let acc = map.get(nctId);
    if (!acc) {
      acc = {
        nctId,
        description: r.description,
        briefSummary: r.briefSummary,
        sponsorInfo: r.sponsorInfo,
        eligibilityCriteria: r.eligibility?.eligibilityCriteria,
        healthyVolunteers: r.eligibility?.healthyVolunteers,
        sex: r.eligibility?.sex,
        minimumAge: r.eligibility?.minimumAge,
        site_location: [],
        siteKeySet: new Set<string>(),
      };
      map.set(nctId, acc);
    }

    // first-non-empty for trial-level fields
    if (!acc.description && r.description) acc.description = r.description;
    if (!acc.briefSummary && r.briefSummary) acc.briefSummary = r.briefSummary;
    if (!acc.sponsorInfo && r.sponsorInfo) acc.sponsorInfo = r.sponsorInfo;

    // eligibility: first-non-empty / first-defined
    if (!acc.eligibilityCriteria && r.eligibility?.eligibilityCriteria) {
      acc.eligibilityCriteria = r.eligibility.eligibilityCriteria;
    }
    if (acc.healthyVolunteers === undefined && r.eligibility?.healthyVolunteers !== undefined) {
      acc.healthyVolunteers = r.eligibility.healthyVolunteers;
    }
    if (!acc.sex && r.eligibility?.sex) acc.sex = r.eligibility.sex;
    if (!acc.minimumAge && r.eligibility?.minimumAge) acc.minimumAge = r.eligibility.minimumAge;

    // merge location
    const loc: SiteLocation = {
      city: r.city,
      state: r.state,
      zip: r.zip,
      lat: r.lat,
      lng: r.lng,
    };

    const hasAny =
      loc.city || loc.state || loc.zip || loc.lat !== undefined || loc.lng !== undefined;

    if (hasAny) {
      const k = locKey(loc);
      if (!acc.siteKeySet.has(k)) {
        acc.siteKeySet.add(k);
        acc.site_location.push(loc);
      }
    }
  }

  return Array.from(map.values()).map(({ siteKeySet, ...trial }) => trial);
};

// Helper function to render trial information on table
const toTrialTable = (trials: ClinicalTrial[]): TrialTable[] => {
  return trials.map((t) => {
    const states = new Set(
      (t.site_location ?? [])
        .map((l) => l.state?.trim())
        .filter((s): s is string => Boolean(s))
    );

    const state =
      states.size === 0 ? '' :
      states.size === 1 ? Array.from(states)[0] :
      'Multiple';

    return {
      nctId: t.nctId,
      title: t.description ?? '',
      sponsor: t.sponsorInfo?.leadSponsor?.name ?? '',
      state,
    };
  });
};

export function MatcherLoading({ active }: { active: boolean }) {
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!active) {
      setStepIndex(0); // reset when not loading
      return;
    }

    setStepIndex(0);
    const id = window.setInterval(() => {
      setStepIndex((prev) => (prev < PROGRESS_STEPS.length - 1 ? prev + 1 : prev));
    }, 10000);

    return () => window.clearInterval(id);
  }, [active]);

  return (
    <Stack gap={1} direction="row" alignItems="center">
      <CircularProgress sx={{color:'rgb(219, 103, 32)'}}/>
      <Typography variant="body2" color="text.secondary">
        {PROGRESS_STEPS[stepIndex]}
      </Typography>
    </Stack>
  );
}


export const BCAN_Trials: FC = () => {
  // Search Menu Dropdown hooks
  const [cancerType, setCancerType] = React.useState('All');
  const [state, setState] = React.useState('All');
  const [sort, setSort] = React.useState('Default');
  const [selectedTrial, setSelectedTrial] = useState<ClinicalTrial | null>(null);
  const { data: clinicalTrials, isLoading: clinicalTrialsIsLoading, isSuccess: clinicalTrialsIsSuccess, isError: clinicalTrialsIsError, error: clinicalTrialsError, } = useGetClinicalTrialsQuery({condition: 'Bladder Cancer'})
  const [matchedTrialsAI, setMatchedTrialsAI] = useState<MatchTrialsResponse | null>(null);
  const [matchTrials,{ data, isLoading: matchingAIisLoading, isSuccess, isError, error }] = useMatchTrialsMutation();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  
  // Filtering logic after AI matching tool is called
  const matchedNctIdSet = useMemo(() => {
    const ids = matchedTrialsAI?.matches.map(m => m.nctId.trim()) ?? [];
    return new Set(ids);
  }, [matchedTrialsAI]);

  const normalizedTrials = useMemo<ClinicalTrial[]>(() => {
    return normalizeTrials((clinicalTrials ?? []) as ClinicalTrialRaw[]);
  }, [clinicalTrials]);

  const filteredTrials = useMemo(() => {
    if (!matchedTrialsAI?.matches?.length) return normalizedTrials;

    return normalizedTrials.filter(t =>
      t.nctId && matchedNctIdSet.has(t.nctId.trim())
    );
  }, [normalizedTrials, matchedTrialsAI, matchedNctIdSet]);

  // If AI result - get match reason by ID
  const matchReasonById = useMemo(() => {
    const m = new Map<string, string>();
    matchedTrialsAI?.matches.forEach(({ nctId, reason }) => {
      m.set(nctId.trim(), reason);
    });
    return m;
  }, [matchedTrialsAI]);


  // Menu data lists
  const cancer_type_menu = [
    {value:'All', display: 'All'},
    {value:'Upper Tract Urothelial Carcinoma', display:'Upper Tract Urothelial Carcinoma (UTUC-low and high grade)'},
    {value:'Non-Muscle Invasive Bladder Cancer', display:'Non-Muscle Invasive Bladder Cancer (Includes Ta, T1, CIS Tumors)'},
    {value:'Muscle-Invasive Bladder Cancer', display:'Muscle-Invasive Bladder Cancer (includes T2 Tumors)'},
    {value:'Locally Advanced', display:'Locally Advanced (includes T3 Tumors)'},
    {value:'Metastatic Bladder Cancer', display:'Metastatic Bladder Cancer (includes T4 Tumors)'}
  ]

  const state_menu = [{"name":"All","code":"All"},{"name":"Alabama","code":"AL"},{"name":"Alaska","code":"AK"},{"name":"Arizona","code":"AZ"},{"name":"Arkansas","code":"AR"},{"name":"California","code":"CA"},{"name":"Colorado","code":"CO"},{"name":"Connecticut","code":"CT"},{"name":"Delaware","code":"DE"},{"name":"Florida","code":"FL"},{"name":"Georgia","code":"GA"},{"name":"Hawaii","code":"HI"},{"name":"Idaho","code":"ID"},{"name":"Illinois","code":"IL"},{"name":"Indiana","code":"IN"},{"name":"Iowa","code":"IA"},{"name":"Kansas","code":"KS"},{"name":"Kentucky","code":"KY"},{"name":"Louisiana","code":"LA"},{"name":"Maine","code":"ME"},{"name":"Maryland","code":"MD"},{"name":"Massachusetts","code":"MA"},{"name":"Michigan","code":"MI"},{"name":"Minnesota","code":"MN"},{"name":"Mississippi","code":"MS"},{"name":"Missouri","code":"MO"},{"name":"Montana","code":"MT"},{"name":"Nebraska","code":"NE"},{"name":"Nevada","code":"NV"},{"name":"New Hampshire","code":"NH"},{"name":"New Jersey","code":"NJ"},{"name":"New Mexico","code":"NM"},{"name":"New York","code":"NY"},{"name":"North Carolina","code":"NC"},{"name":"North Dakota","code":"ND"},{"name":"Ohio","code":"OH"},{"name":"Oklahoma","code":"OK"},{"name":"Oregon","code":"OR"},{"name":"Pennsylvania","code":"PA"},{"name":"Rhode Island","code":"RI"},{"name":"South Carolina","code":"SC"},{"name":"South Dakota","code":"SD"},{"name":"Tennessee","code":"TN"},{"name":"Texas","code":"TX"},{"name":"Utah","code":"UT"},{"name":"Vermont","code":"VT"},{"name":"Virginia","code":"VA"},{"name":"Washington","code":"WA"},{"name":"West Virginia","code":"WV"},{"name":"Wisconsin","code":"WI"},{"name":"Wyoming","code":"WY"}]

  const sort_menu = [
    {value:'Default', display:'Default'},
    {value:'Nearest Me', display:'Nearest Me'},
    {value:'Date Added', display:'Date Added'},
    {value:'Title', display:'Title'},
  ]

  //Helper functions to filter through trial data
  const trialByNctId = useMemo(() => {
    const m = new Map<string, ClinicalTrial>();
    for (const t of filteredTrials) {
      if (t.nctId) m.set(t.nctId, t);
    }
    return m;
  }, [filteredTrials]);


  // Menu handle change functions
  const handleCancerTypeChange = (event: SelectChangeEvent) => {
    setCancerType(event.target.value as string);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    setState(event.target.value as string);
  };

  const handleStateChange = (event: SelectChangeEvent) => {
    setSort(event.target.value as string);
  };

  const handleResetTable = () => {
    // 1. Clear AI match results → removes filtering
    setMatchedTrialsAI(null);

    // 2. Exit trial detail view if open
    setSelectedTrial(null);

    // 3. Clear the text input (uncontrolled input)
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleAIMatcherSubmit = async () => {

    const value = inputRef.current?.value ?? '';
    const textField = value.trim();
    if (!textField) return;

    try {
      const response = await matchTrials({
        prompt: textField,
        trials: filteredTrials,
      }).unwrap();

      if (!response?.matches?.length) return;

      setMatchedTrialsAI(response);

    } catch (e) {
      console.error('AI matcher failed:', e);
    }
  };

  // Table data
  const tableData = useMemo<TrialTable[]>(() => {
    return toTrialTable(filteredTrials);
  }, [filteredTrials]);

  //Table Columns
  const columns = useMemo<MRT_ColumnDef<TrialTable>[]>(() => {
    const cols: MRT_ColumnDef<TrialTable>[] = [];

    // Only add this column when AI results exist
    if (matchedTrialsAI?.matches?.length) {
      cols.push({
        header: 'Why this matches',
        Cell: ({ row }) => {
          const id = row.original.nctId;
          return id ? matchReasonById.get(id) ?? '' : '';
        },
      });
    }

    cols.push(
      {
        accessorKey: 'title',
        header: 'Title',
        enableHiding: false,
        Cell: ({ row }) => {
          const nctId = row.original.nctId;
          return (
            <Link
              component="button"
              underline="hover"
              sx={{ textAlign: 'left', color: 'rgb(219, 103, 32)' }}
              onClick={() => {
                if (!nctId) return;
                setSelectedTrial(trialByNctId.get(nctId) ?? null);
              }}
            >
              {row.original.title}
            </Link>
          );
        },
      },
      { accessorKey: 'state', header: 'State' },
      { accessorKey: 'sponsor', header: 'Sponsor' },
    );

    return cols;
  }, [matchedTrialsAI, matchReasonById, trialByNctId]);

  //pass table options to useMaterialReactTable
  const table = useMaterialReactTable({
    columns,
    data: tableData, //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableRowSelection: false, //enable some features
    enableColumnOrdering: true, //enable a feature for all columns
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false, //turn off a feature
    enableTopToolbar:false,
    initialState: { pagination: { pageSize: 50, pageIndex: 0 } }, //customize the default page size and page index
    muiTablePaperProps: {
      elevation: 0, //change the mui box shadow
    },
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      showRowsPerPage: false,
      variant: 'outlined',
    },
    muiTableContainerProps: {
      sx: {
        height: 'auto',
        maxHeight: 'none',
        overflowY: 'visible', // <-- kill vertical internal scroll
        overflowX: 'auto',    // <-- keep horizontal scroll if needed
      },
    },
    paginationDisplayMode: 'pages',
  });

  //Render
  if (selectedTrial) {
    return (
      <TrialDetails
        trial={selectedTrial}
        onBack={() => setSelectedTrial(null)}
      />
    );
  } 


  return (
    <Box sx={{py:5, width:'100%', height:'100%', overflow:'auto'}}>
    <Container>
      <Stack
       gap={2}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          justifyItems: 'center',
        }}
      >
        <Grid container spacing={4} sx={{pl:8, pr:8, pt:2, pb:2, borderRadius: 4, backgroundColor:'rgb(247, 247, 247)'}}>
          <Grid size={3} sx={{width:200}}>
            <Stack gap={1}>
              <Typography variant="subtitle2" sx={{color:'gray', fontWeight:'bold'}}>
                Bladder Cancer Type:
              </Typography>
              <FormControl fullWidth variant="filled" size="small">
                <Select
                  value={cancerType}
                  onChange={handleCancerTypeChange}
                >
                  {cancer_type_menu.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.display}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Grid>
          <Grid size={3} sx={{width:200}}>
            <Stack gap={1}>
              <Typography variant="subtitle2" sx={{color:'gray', fontWeight:'bold'}}>
                State:
              </Typography>
              <FormControl fullWidth variant="filled" size="small">
                <Select
                  value={state}
                  onChange={handleStateChange}
                >
                  {state_menu.map((item) => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Grid>
          <Grid size={3} sx={{width:200}}>
            <Stack gap={1}>
              <Typography variant="subtitle2" sx={{color:'gray', fontWeight:'bold'}}>
                Sort by:
              </Typography>
              <FormControl fullWidth variant="filled" size="small">
                <Select
                  value={sort}
                  onChange={handleSortChange}
                >
                  {sort_menu.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.display}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Grid>
          <Grid size={3} sx={{width:100, display:'flex', flexDirection:'column'}}>
            <Stack spacing={1} direction="row" sx={{mt:'auto'}}>
              <Button size="large" variant="contained" disableElevation sx={{ backgroundColor:'rgb(219, 103, 32)',px:1, minWidth:'auto'}}>
                Submit
              </Button>
              <IconButton size="large" sx={{backgroundColor:'rgb(219, 103, 32)', color:'white', '&:hover': {backgroundColor: 'rgb(219, 103, 32)'}}}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Grid>
        </Grid>
        <Stack 
          gap={1}
          sx={{
            width:'100%',
            p:2,
            backgroundColor:'rgb(247, 247, 247)',
            borderRadius: 4,
          }}
        >
          <Typography variant="h6" sx={{color:'rgb(219, 103, 32)', fontWeight:'bold'}}>
            <AutoAwesomeIcon/> Describe and match
          </Typography>
          {!matchingAIisLoading 
          ?
            <>
            <TextField
              label="Write about your condition and our AI Assistant will recommend clinical trials"
              placeholder="I was diagnosed with non-muscle invasive bladder cancer last year. I’ve already had TURBT surgery and BCG treatment, but my cancer has come back. I’m 67 years old, male, and generally in good health. I’m looking for clinical trials that might be an option for me."
              multiline
              rows={4}
              inputRef={inputRef}
              fullWidth
              sx={{
                backgroundColor: 'white',

                // default border
                '& .MuiOutlinedInput-root fieldset': {
                  borderColor: '#ccc',
                },

                // hover border
                '& .MuiOutlinedInput-root:hover fieldset': {
                  borderColor: '#888',
                },

                // focused border (this removes the blue)
                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                  borderColor: 'rgb(219, 103, 32)', // ← your focus color
                  borderWidth: 2,
                },

              '& label': {
                  color: 'rgb(219, 103, 32)',
                },

                // label color when focused (removes the blue)
                '& label.Mui-focused': {
                  color: 'rgb(219, 103, 32)', // ← your color
                },
              }}
            />
            <Stack gap={1} direction="row">
              <Button onClick={handleAIMatcherSubmit} size="small" variant="contained" disableElevation sx={{ backgroundColor:'rgb(219, 103, 32)', alignSelf:'flex-start'}}>
                Submit
              </Button>
              <IconButton onClick={handleResetTable} size="small" sx={{backgroundColor:'rgb(219, 103, 32)', color:'white', '&:hover': {backgroundColor: 'rgb(219, 103, 32)'}}}>
                <RefreshIcon />
              </IconButton>
            </Stack>
            </>
          :
            <MatcherLoading active={matchingAIisLoading} />
          }
        </Stack>
        <Box sx={{px:4, py:4, backgroundColor:'rgb(247, 247, 247)', width:'100%', borderRadius:4}}>
          <Typography variant="h6" sx={{color:'rgb(219, 103, 32)', fontWeight:'bold'}}>
            Featured Trials
          </Typography>
          {!matchingAIisLoading 
            ? <MaterialReactTable table={table}/> 
            : <Skeleton variant="rounded" width='100%' height={400} />
          }
        </Box>
      </Stack>
    </Container>
    </Box>
  )
}
